import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';

// Core pages - loaded immediately
import LandingPage from './pages/LandingPage';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';

// Lazy-loaded pages for better initial load performance
const InteractiveTools = lazy(() => import('./pages/InteractiveTools'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const SubscriptionConfirmed = lazy(() => import('./pages/SubscriptionConfirmed'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));

// Dashboard - only available in development
const Dashboard = import.meta.env.DEV
  ? lazy(() => import('./pages/dashboard/Dashboard'))
  : null;

// Lazy-loaded interactive tools
const HydrogenBlendCalculator = lazy(() => import('./pages/interactive-tools/HydrogenBlendCalculator'));
const TelemetrySimulator = lazy(() => import('./pages/interactive-tools/TelemetrySimulator'));
const ScadaDashboard = lazy(() => import('./pages/interactive-tools/ScadaDashboard'));
const LcoeRetrofit = lazy(() => import('./pages/interactive-tools/LcoeRetrofit'));
const EmissionsEstimator = lazy(() => import('./pages/interactive-tools/EmissionsEstimator'));
const CombustionExplorer = lazy(() => import('./pages/interactive-tools/CombustionExplorer'));
const PeakerScheduler = lazy(() => import('./pages/interactive-tools/PeakerScheduler'));
const H2ReadyChecklist = lazy(() => import('./pages/interactive-tools/H2ReadyChecklist'));
const DataCenterScenarios = lazy(() => import('./pages/interactive-tools/DataCenterScenarios'));
const AlarmRules = lazy(() => import('./pages/interactive-tools/AlarmRules'));
const AnomalyPlayground = lazy(() => import('./pages/interactive-tools/AnomalyPlayground'));
const TagMapper = lazy(() => import('./pages/interactive-tools/TagMapper'));
const EcoSavingsCalculatorPage = lazy(() => import('./pages/interactive-tools/EcoSavingsCalculatorPage'));
const H2MaterialPredictor = lazy(() => import('./pages/interactive-tools/H2MaterialPredictor'));
const H2PurityNOxPlotter = lazy(() => import('./pages/interactive-tools/H2PurityNOxPlotter'));
const TurbineReliabilityBenchmarker = lazy(() => import('./pages/interactive-tools/TurbineReliabilityBenchmarker'));
const DiluentCalculator = lazy(() => import('./pages/interactive-tools/DiluentCalculator'));
const FlightCarbonCalculator = lazy(() => import('./pages/interactive-tools/FlightCarbonCalculator'));
const EmissionsUnitConverter = lazy(() => import('./pages/interactive-tools/EmissionsUnitConverter'));
const Section45VEstimator = lazy(() => import('./pages/interactive-tools/Section45VEstimator'));
const GasTurbineComparison = lazy(() => import('./pages/interactive-tools/GasTurbineComparison'));
const HydrogenBreakevenCalculator = lazy(() => import('./pages/interactive-tools/HydrogenBreakevenCalculator'));
const DataCenterPowerSizer = lazy(() => import('./pages/interactive-tools/DataCenterPowerSizer'));
const CarbonPriceImpactModeler = lazy(() => import('./pages/interactive-tools/CarbonPriceImpactModeler'));
const HydrogenBlendDispatchOptimizer = lazy(() => import('./pages/interactive-tools/HydrogenBlendDispatchOptimizer'));
const GasTurbineEfficiencyMap = lazy(() => import('./pages/interactive-tools/GasTurbineEfficiencyMap'));
const TurbineInletCoolingROICalculator = lazy(() => import('./pages/interactive-tools/TurbineInletCoolingROICalculator'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

function usePageTracking() {
  const location = useLocation();
  useEffect(() => {
    const page_path = location.pathname + location.search;
    if (window.gtag) window.gtag('config', 'G-C8T32201M4', { page_path });

    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location]);
}

function AppRoutes() {
  usePageTracking();

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />

      {/* Blog */}
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />

      {/* About & Contact */}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />

      {/* Policy Pages */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />

      {/* Subscription Confirmation */}
      <Route path="/confirmed" element={<SubscriptionConfirmed />} />

      {/* Dashboard - only in development */}
      {import.meta.env.DEV && Dashboard && (
        <Route path="/dashboard" element={<Dashboard />} />
      )}

      {/* Hub + tools */}
      <Route path="/interactive-tools" element={<InteractiveTools />} />
      <Route path="/interactive-tools/h2-material-predictor" element={<H2MaterialPredictor />} />
      <Route path="/interactive-tools/h2-purity-nox-plotter" element={<H2PurityNOxPlotter />} />
      <Route path="/interactive-tools/turbine-reliability-benchmarker" element={<TurbineReliabilityBenchmarker />} />
      <Route path="/interactive-tools/diluent-calculator" element={<DiluentCalculator />} />
      <Route path="/interactive-tools/hydrogen-blend-calculator" element={<HydrogenBlendCalculator />} />
      <Route path="/interactive-tools/telemetry-simulator" element={<TelemetrySimulator />} />
      <Route path="/interactive-tools/scada-dashboard" element={<ScadaDashboard />} />
      <Route path="/interactive-tools/lcoe-retrofit" element={<LcoeRetrofit />} />
      <Route path="/interactive-tools/emissions-estimator" element={<EmissionsEstimator />} />
      <Route path="/interactive-tools/combustion-explorer" element={<CombustionExplorer />} />
      <Route path="/interactive-tools/peaker-scheduler" element={<PeakerScheduler />} />
      <Route path="/interactive-tools/h2-ready-checklist" element={<H2ReadyChecklist />} />
      <Route path="/interactive-tools/datacenter-scenarios" element={<DataCenterScenarios />} />
      <Route path="/interactive-tools/alarm-rules" element={<AlarmRules />} />
      <Route path="/interactive-tools/anomaly-playground" element={<AnomalyPlayground />} />
      <Route path="/interactive-tools/tag-mapper" element={<TagMapper />} />
      <Route path="/interactive-tools/eco-savings-calculator" element={<EcoSavingsCalculatorPage />} />
      <Route path="/interactive-tools/flight-carbon-calculator" element={<FlightCarbonCalculator />} />
      <Route path="/interactive-tools/emissions-unit-converter" element={<EmissionsUnitConverter />} />
      <Route path="/interactive-tools/45v-credit-estimator" element={<Section45VEstimator />} />
      <Route path="/interactive-tools/turbine-comparison" element={<GasTurbineComparison />} />
      <Route path="/interactive-tools/h2-breakeven" element={<HydrogenBreakevenCalculator />} />
      <Route path="/interactive-tools/data-center-power-sizer" element={<DataCenterPowerSizer />} />
      <Route path="/interactive-tools/carbon-price-impact-modeler" element={<CarbonPriceImpactModeler />} />
      <Route path="/interactive-tools/hydrogen-blend-dispatch-optimizer" element={<HydrogenBlendDispatchOptimizer />} />
      <Route path="/interactive-tools/gas-turbine-efficiency-map" element={<GasTurbineEfficiencyMap />} />
      <Route path="/interactive-tools/turbine-inlet-cooling-roi-calculator" element={<TurbineInletCoolingROICalculator />} />

      {/* Back-compat */}
      <Route path="/tools" element={<Navigate to="/interactive-tools" replace />} />
      <Route path="/tools/hydrogen-blend-calculator" element={<Navigate to="/interactive-tools/hydrogen-blend-calculator" replace />} />
      <Route path="/tools/telemetry-simulator" element={<Navigate to="/interactive-tools/telemetry-simulator" replace />} />
      <Route path="/tools/scada-dashboard" element={<Navigate to="/interactive-tools/scada-dashboard" replace />} />
      <Route path="/tools/lcoe-retrofit" element={<Navigate to="/interactive-tools/lcoe-retrofit" replace />} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="min-h-screen bg-white">
      <Suspense fallback={<PageLoader />}>
        <AppRoutes />
      </Suspense>
    </div>
  );
}
